import React, { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSession, useSupabaseClient } from '@supabase/auth-helpers-react';
import { getTutorApplications, uploadTutorProof, submitTutorApplication, getPublicSetting, submitAppeal, getPracticeLanguages } from '../api/supabaseAPI';
import Loader from '../components/Loader';
import Message from '../components/Message';
import ModernCombobox from '../components/ModernCombobox';
import AppealChatModal from '../components/AppealChatModal';
import '../styles/TutorApplication.css';
import '../styles/Layout.css';
import '../styles/Introduction.css';

const R2_PUBLIC_BASE_URL_ENV = (import.meta.env.VITE_R2_PUBLIC_BASE_URL || '').replace(/\/$/, '');

export default function TutorApplication() {
  const { t } = useTranslation();
  const session = useSession();
  const supabase = useSupabaseClient();
  const userId = session?.user?.id;
  const queryClient = useQueryClient();

  const [viewMode, setViewMode] = useState('dashboard'); // 'dashboard' | 'form'
const [rejectModal, setRejectModal] = useState({ open: false, reason: '', language: '' });
  const [pauseModalLanguage, setPauseModalLanguage] = useState(null); // ✨ Only store language, get data from query
  const [showFullReason, setShowFullReason] = useState(false); // ✨ Expandable reason state
  const [appealMessage, setAppealMessage] = useState('');
  const [formData, setFormData] = useState({
    language: '',
    full_name: '',
    self_introduction: '',
    proofs: [{ id: 1, name: '', file: null, preview: null }]
  });
  const [formMessage, setFormMessage] = useState({ text: '', type: '' });

  // Cleanup object URLs on unmount
  useEffect(() => {
    return () => {
      formData.proofs.forEach(proof => {
        if (proof.preview) URL.revokeObjectURL(proof.preview);
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reset reason expansion when modal opens/changes
  useEffect(() => {
    if (pauseModalLanguage) {
      setShowFullReason(false);
    }
  }, [pauseModalLanguage]);

  const { data: r2PublicBaseUrl } = useQuery({
    queryKey: ['public_settings', 'r2_public_base_url'],
    queryFn: () => getPublicSetting('r2_public_base_url'),
    staleTime: 1000 * 60 * 30,
    refetchOnMount: false,
  });
  const { data: practiceLanguages = [], isLoading: isLoadingPracticeLanguages } = useQuery({
    queryKey: ['practiceLanguages'],
    queryFn: getPracticeLanguages,
    staleTime: 1000 * 60 * 30,
    refetchOnMount: false,
  });

  const publicBaseUrl = useMemo(() => {
    const rawBaseUrl = R2_PUBLIC_BASE_URL_ENV || r2PublicBaseUrl || '';
    return rawBaseUrl ? rawBaseUrl.replace(/\/$/, '') : '';
  }, [r2PublicBaseUrl]);

  const practiceLanguageMap = useMemo(() => (
    new Map(practiceLanguages.map((item) => [item.value, item.label]))
  ), [practiceLanguages]);

  const { data: applications, isLoading } = useQuery({
    queryKey: ['tutorApplications', userId],
    queryFn: () => getTutorApplications(userId),
    enabled: !!userId,
    staleTime: 0,  // ✨ 永遠視為過期
    refetchOnMount: 'always',  // ✨ 每次進入頁面都重新請求
  });

  // ✨ Realtime subscription for Appeal messages
  useEffect(() => {
    if (!userId || !pauseModalLanguage) return;

    const channel = supabase
      .channel('tutor-appeal-updates')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'tutor_applications',
          filter: `uid=eq.${userId}`
        },
        (payload) => {
          // 當 appeal_message 更新時，刷新資料
          if (payload.new?.language === pauseModalLanguage) {
            queryClient.invalidateQueries({ queryKey: ['tutorApplications', userId] });
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [userId, pauseModalLanguage, supabase, queryClient]);

  const submitMutation = useMutation({
    mutationFn: submitTutorApplication,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tutorApplications', userId] });
      
      // Cleanup previews before resetting
      formData.proofs.forEach(proof => {
        if (proof.preview) URL.revokeObjectURL(proof.preview);
      });

      setViewMode('dashboard');
      setFormData({
        language: '',
        full_name: '',
        self_introduction: '',
        proofs: [{ id: 1, name: '', file: null, preview: null }]
      });
      setFormMessage({ text: '', type: '' });
    },
    onError: (error) => {
      setFormMessage({ text: error.message || t('tutorApp.messages.submitFailed'), type: 'error' });
    }
  });

  // Filter available languages (rejected and paused applications don't count as taken)
  const availableLanguages = useMemo(() => {
    if (!applications) return practiceLanguages;
    const takenLanguages = new Set(
      applications
        .filter(app => app.status !== 'rejected' && app.status !== 'paused')
        .map(app => app.language)
    );
    return practiceLanguages.filter(lang => !takenLanguages.has(lang.value));
  }, [applications, practiceLanguages]);

  // ✨ Appeal Mutation with Optimistic UI
  const appealMutation = useMutation({
    mutationFn: submitAppeal,
    onMutate: async (newAppeal) => {
      // 1. Cancel any outgoing refetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({ queryKey: ['tutorApplications', userId] });

      // 2. Snapshot the previous value
      const previousApplications = queryClient.getQueryData(['tutorApplications', userId]);

      // 3. Optimistically update to the new value
      queryClient.setQueryData(['tutorApplications', userId], (old) => {
        if (!old) return [];
        return old.map((app) => {
          if (app.language === newAppeal.language) {
            const newMessage = {
              role: 'user', // user role
              message: newAppeal.message,
              at: new Date().toISOString(), // fake timestamp
            };
            return {
              ...app,
              appeal_message: app.appeal_message ? [...app.appeal_message, newMessage] : [newMessage],
            };
          }
          return app;
        });
      });

      // Return a context object with the snapshotted value
      return { previousApplications };
    },
    onError: (err, newAppeal, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      queryClient.setQueryData(['tutorApplications', userId], context.previousApplications);
      alert(err.message || t('tutorApp.messages.appealFailed'));
    },
    onSettled: () => {
      // Always refetch after error or success:
      queryClient.invalidateQueries({ queryKey: ['tutorApplications', userId] });
    },
  });

  const handleSendAppeal = (messageText) => {
    if (!messageText?.trim() || !pauseModalLanguage) return;
    
    // ✨ Clear input handled by Modal now
    // setAppealMessage(''); 

    appealMutation.mutate({
      uid: userId,
      language: pauseModalLanguage,
      message: messageText
    });
  };

  // Find the active application for the modal
  const activePauseApp = useMemo(() => {
    if (!pauseModalLanguage || !applications) return null;
    return applications.find(app => app.language === pauseModalLanguage);
  }, [applications, pauseModalLanguage]);

  const formatAppealTime = (isoString) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleProofChange = (id, field, value) => {
    setFormData(prev => ({
      ...prev,
      proofs: prev.proofs.map(p => p.id === id ? { ...p, [field]: value } : p)
    }));
  };

  const handleProofFileChange = (id, event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    // Revoke old preview
    const oldProof = formData.proofs.find(p => p.id === id);
    if (oldProof?.preview) URL.revokeObjectURL(oldProof.preview);

    const preview = URL.createObjectURL(file);
    setFormData(prev => ({
      ...prev,
      proofs: prev.proofs.map(p => p.id === id ? { ...p, file, preview } : p)
    }));
  };

  const addProofSection = () => {
    setFormData(prev => ({
      ...prev,
      proofs: [...prev.proofs, { id: Date.now(), name: '', file: null, preview: null }]
    }));
  };

  const removeProofSection = (id) => {
    setFormData(prev => ({
      ...prev,
      proofs: prev.proofs.filter(p => p.id !== id)
    }));
    
    // Revoke preview of removed item
    const removedProof = formData.proofs.find(p => p.id === id);
    if (removedProof?.preview) {
      URL.revokeObjectURL(removedProof.preview);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormMessage({ text: t('tutorApp.messages.submitting'), type: 'info' });

    try {
      if (!formData.language || !formData.full_name || !formData.self_introduction) {
        throw new Error(t('tutorApp.messages.fillRequired'));
      }

      if (formData.proofs.some(p => !p.name || !p.file)) {
        throw new Error(t('tutorApp.messages.proofRequired'));
      }

      // 1. Upload all images
      const uploadPromises = formData.proofs.map(async (proof) => {
        const path = await uploadTutorProof(proof.file, formData.language);
        return { name: proof.name, path };
      });

      const uploadedProofs = await Promise.all(uploadPromises);

      // 2. Submit application
      await submitMutation.mutateAsync({
        uid: userId,
        language: formData.language,
        full_name: formData.full_name,
        self_introduction: formData.self_introduction,
        proof_images: uploadedProofs // JSON array
      });

    } catch (err) {
      setFormMessage({ text: err.message, type: 'error' });
    }
  };

  if (isLoading || isLoadingPracticeLanguages) return <main className="tutor-page-container"><Loader /></main>;

  return (
    <main className="tutor-page-container">
      {viewMode === 'dashboard' ? (
        <>
          <h1 className="page-title">{t('tutorApp.title', 'Tutor Applications')}</h1>
          <header className="tutor-app-header">
            <p className="tutor-page-subtitle">{t('tutorApp.subtitle')}</p>
          </header>

          <div className="tutor-app-grid">
            {applications?.map((app) => {
              const langLabel = practiceLanguageMap.get(app.language) || app.language;
              const statusKey = String(app.status || '').toLowerCase();
              const statusLabel =
                statusKey === 'approved' || statusKey === 'active'
                  ? t('tutorApp.status.active')
                  : statusKey === 'pending'
                    ? t('tutorApp.status.pending')
                    : statusKey === 'rejected'
                      ? t('tutorApp.status.rejected')
                      : statusKey === 'paused'
                        ? t('tutorApp.status.paused')
                        : (app.status ?? '').toUpperCase();
              const isRejected = statusKey === 'rejected';
              const isPaused = statusKey === 'paused';
              return (
                <div 
                  key={`${app.uid}-${app.language}`} 
                  className="tutor-card"
                >
                  <div className="tutor-card-header">
                    <div className="tutor-language-title">{langLabel}</div>
                    <span className={`tutor-status-badge status-${statusKey}`}>
                      {statusLabel}
                    </span>
                  </div>
                  <div className="tutor-card-body">
                    {isRejected && (
                      <p className="error-text">{t('tutorApp.dashboard.rejected')}</p>
                    )}
                    {isPaused && (
                      <p className="warning-text">{t('tutorApp.dashboard.paused')}</p>
                    )}
                    {(statusKey === 'approved' || statusKey === 'active') && (
                      <p>{t('tutorApp.dashboard.active')}</p>
                    )}
                    {statusKey === 'pending' && (
                      <p>{t('tutorApp.dashboard.pending')}</p>
                    )}
                  </div>
                  {isRejected && (
                    <button 
                      className="view-reason-btn"
                      onClick={() => setRejectModal({
                        open: true,
                        reason: app.reason || t('tutorApp.dashboard.noReason'),
                        language: langLabel
                      })}
                    >
                      {t('tutorApp.dashboard.viewReason')}
                    </button>
                  )}
                  {isPaused && (
                    <button 
                      className="view-reason-btn btn-paused"
                      onClick={() => {
                        setPauseModalLanguage(app.language);
                        setAppealMessage('');
                      }}
                    >
                      {t('tutorApp.dashboard.viewDetailsAppeal')}
                    </button>
                  )}
                </div>
              );
            })}

            {availableLanguages.length > 0 && (
              <button className="tutor-card tutor-card-add" onClick={() => setViewMode('form')}>
                <div className="add-icon-circle">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 5v14M5 12h14" />
                  </svg>
                </div>
                <span>{t('tutorApp.dashboard.applyAnotherLanguage')}</span>
              </button>
            )}
          </div>
        </>
      ) : (
        <div className="tutor-form-shell">
          <div className="tutor-form-header">
            <button className="back-btn" onClick={() => setViewMode('dashboard')} aria-label={t('tutorApp.form.goBack')}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M19 12H5M12 19l-7-7 7-7" />
              </svg>
            </button>
            <h2 className="tutor-form-title">{t('tutorApp.form.title')}</h2>
          </div>

          <form onSubmit={handleSubmit} className="tutor-form-content">
            <div className="tutor-form-body">
              <section className="form-section">
                <h3 className="section-subtitle">{t('tutorApp.form.basicInfo')}</h3>
                <div className="section-content">
                  <ModernCombobox
                    label={t('tutorApp.form.language')}
                    value={formData.language}
                    onChange={(val) => handleInputChange({ target: { name: 'language', value: val } })}
                    placeholder={t('tutorApp.form.selectLanguage')}
                    options={availableLanguages}
                  />

                  <div className="input-group">
                    <label>{t('tutorApp.form.fullName')}</label>
                    <input
                      type="text"
                      name="full_name"
                      value={formData.full_name}
                      onChange={handleInputChange}
                      placeholder={t('tutorApp.form.fullNamePlaceholder')}
                      required
                    />
                  </div>

                  <div className="input-group">
                    <label>{t('tutorApp.form.selfIntroduction')}</label>
                    <textarea
                      name="self_introduction"
                      value={formData.self_introduction}
                      onChange={handleInputChange}
                      placeholder={t('tutorApp.form.selfIntroductionPlaceholder')}
                      rows={6}
                      required
                    />
                  </div>
                </div>
              </section>

              <section className="form-section">
                <h3 className="section-subtitle">{t('tutorApp.form.credentials')}</h3>
                <div className="section-content">
                  <div className="input-group">
                    <label>{t('tutorApp.form.proofDocuments')}</label>
                    <div className="proof-list">
                      {formData.proofs.map((proof, index) => (
                        <div key={proof.id} className="proof-card">
                          <div className="proof-card-header">
                            <span className="proof-number">{t('tutorApp.form.documentNumber', { number: index + 1 })}</span>
                            {index > 0 && (
                              <button 
                                type="button" 
                                className="proof-remove-btn"
                                onClick={() => removeProofSection(proof.id)}
                                aria-label={t('tutorApp.form.removeDocument')}
                              >
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path d="M18 6L6 18M6 6l12 12"/>
                                </svg>
                              </button>
                            )}
                          </div>
                          
                          <div className="input-group">
                            <label>{t('tutorApp.form.documentName')}</label>
                            <input
                              type="text"
                              value={proof.name}
                              onChange={(e) => handleProofChange(proof.id, 'name', e.target.value)}
                              placeholder={t('tutorApp.form.documentNamePlaceholder')}
                              required
                            />
                          </div>
                          
                          <div className="input-group">
                            <label>{t('tutorApp.form.imageUpload')}</label>
                            <input
                              id={`proof-file-${proof.id}`}
                              type="file"
                              accept="image/*"
                              className="file-input-hidden"
                              onChange={(e) => handleProofFileChange(proof.id, e)}
                              // required={!proof.file} // Manual validation handles this better
                            />
                            <label 
                              htmlFor={`proof-file-${proof.id}`} 
                              className={`file-upload-label ${proof.file ? 'has-file' : ''}`}
                            >
                              <svg data-testid="geist-icon" height="16" strokeLinejoin="round" viewBox="0 0 16 16" width="16" >
                                <path fillRule="evenodd" clipRule="evenodd" d="M14.5 2.5H1.5V9.18933L2.96966 7.71967L3.18933 7.5H3.49999H6.63001H6.93933L6.96966 7.46967L10.4697 3.96967L11.5303 3.96967L14.5 6.93934V2.5ZM8.00066 8.55999L9.53034 10.0897L10.0607 10.62L9.00001 11.6807L8.46968 11.1503L6.31935 9H3.81065L1.53032 11.2803L1.5 11.3106V12.5C1.5 13.0523 1.94772 13.5 2.5 13.5H13.5C14.0523 13.5 14.5 13.0523 14.5 12.5V9.06066L11 5.56066L8.03032 8.53033L8.00066 8.55999ZM4.05312e-06 10.8107V12.5C4.05312e-06 13.8807 1.11929 15 2.5 15H13.5C14.8807 15 16 13.8807 16 12.5V9.56066L16.5607 9L16.0303 8.46967L16 8.43934V2.5V1H14.5H1.5H4.05312e-06V2.5V10.6893L-0.0606689 10.75L4.05312e-06 10.8107Z" fill="currentColor"></path>
                              </svg>
                              <span>{proof.file ? t('tutorApp.form.changeFile') : t('tutorApp.form.chooseFile')}</span>
                            </label>
                            {proof.file && (
                              <div className="file-name-display">{proof.file.name}</div>
                            )}
                          </div>

                          {proof.preview && (
                            <div className="proof-preview-area">
                              <img src={proof.preview} alt={t('tutorApp.form.documentPreview')} />
                            </div>
                          )}
                        </div>
                      ))}
                      
                      <button type="button" className="add-proof-btn" onClick={addProofSection}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M12 5v14M5 12h14" />
                        </svg>
                        {t('tutorApp.form.addAnotherDocument')}
                      </button>
                    </div>
                  </div>
                </div>
              </section>

              {formMessage.text && (
                <Message text={formMessage.text} type={formMessage.type} />
              )}
            </div>

            <div className="tutor-form-footer">
              <button type="button" className="secondary-btn" onClick={() => setViewMode('dashboard')}>
                {t('tutorApp.form.cancel')}
              </button>
              <button type="submit" className="primary-btn" disabled={submitMutation.isLoading}>
                {submitMutation.isLoading ? t('tutorApp.form.submitting') : t('tutorApp.form.submit')}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Reject Reason Modal */}
      {rejectModal.open && (
        <div className="modal-overlay" onClick={() => setRejectModal({ open: false, reason: '', language: '' })}>
          <div className="reject-modal" onClick={(e) => e.stopPropagation()}>
            <div className="reject-modal-header">
              <h3>{t('tutorApp.rejectModal.title')}</h3>
              <button 
                className="modal-close-btn"
                onClick={() => setRejectModal({ open: false, reason: '', language: '' })}
                aria-label={t('tutorApp.rejectModal.close')}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6L6 18M6 6l12 12"/>
                </svg>
              </button>
            </div>
            <div className="reject-modal-body">
              <p className="reject-modal-language">{rejectModal.language}</p>
              <p className="reject-modal-reason">{rejectModal.reason}</p>
            </div>
            <div className="reject-modal-footer">
              <button 
                className="primary-btn"
                onClick={() => setRejectModal({ open: false, reason: '', language: '' })}
              >
                {t('tutorApp.rejectModal.close')}
              </button>
            </div>
          </div>
        </div>
      )}

{/* ✨ Pause/Appeal Modal - Apple Style Redesigned */}
      <AppealChatModal
        isOpen={!!activePauseApp}
        onClose={() => setPauseModalLanguage(null)}
        title={t('tutorApp.appealModal.title')}
        messages={activePauseApp?.appeal_message || []}
        onSendMessage={handleSendAppeal}
        isLoading={appealMutation.isLoading}
        currentUserRole="user"
      >
        {/* Injected Reason Card */}
        {activePauseApp && (
          <div className="ios-reason-card">
            <div className="ios-reason-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                <line x1="12" y1="9" x2="12" y2="13"/>
                <line x1="12" y1="17" x2="12.01" y2="17"/>
              </svg>
            </div>
            <div className="ios-reason-text">
              <span className="ios-reason-label">{t('tutorApp.appealModal.pauseReason')}</span>
              <p className={!showFullReason ? 'ios-line-clamp' : ''}>
                {activePauseApp.reason || t('tutorApp.dashboard.noReason')}
              </p>
              {(activePauseApp.reason && activePauseApp.reason.length > 120) && (
                <button 
                  className="ios-read-more-btn"
                  onClick={() => setShowFullReason(!showFullReason)}
                >
                  {showFullReason ? t('tutorApp.appealModal.showLess') : t('tutorApp.appealModal.more')}
                </button>
              )}
            </div>
          </div>
        )}
      </AppealChatModal>
    </main>
  );
}
